import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled, { css, keyframes } from 'styled-components';
import { useHistory, useLocation } from 'react-router';
import { ActionButton } from '../../_common';
import { TwoKeyIcon } from './Icons';
import heroMap from '../media/hero_map.png';

const loginSlideAnimation = keyframes`
  0% {
    clip-path: polygon(0 0, 40% 0, 40% 100%, 12% 100%);
    z-index: 3;
  }
  70% {
    clip-path: polygon(65% 0, 100% 0, 100% 100%, 77% 100%);
    z-index: 3;
  }
  100% {
    clip-path: polygon(60% 0, 100% 0, 100% 100%, 72% 100%);
    z-index: 1;
  }
`;

const loginContentSlideAnimation = keyframes`
  0% {
    transform: translateX(-60vw);
  }
  75% {
    transform: translateX(5vw);
  }
  100% {
    transform: translateX(0);
  }
`;

const signUpContentSlideAnimation = keyframes`
  0% {
    transform: translateX(60vw);
  }
  75% {
    transform: translateX(-5vw);
  }
  100% {
    transform: translateX(0);
  }
`;

const signUpSlideAnimation = keyframes`
  0% {
    clip-path: polygon(60% 0, 100% 0, 88% 100%, 60% 100%);
    z-index: 3;
  }
  70% {
    clip-path: polygon(0 0, 35% 0, 23% 100%, 0 100%);
    z-index: 3;
  }
  100% {
    clip-path: polygon(0 0, 40% 0, 28% 100%, 0 100%);
    z-index: 1;
  }
`;

const loginStyles = css`
  clip-path: polygon(60% 0, 100% 0, 100% 100%, 72% 100%);
  animation: ${loginSlideAnimation} .5s ease-in-out;
  
  .loginSignUp-wrapper {
    justify-content: flex-end;
    &:before {
      background: #17936f url(${heroMap});
      transform: scaleX(-1);
    }
  }
`;

const signUpStyles = css`
  clip-path: polygon(0 0, 40% 0, 28% 100%, 0 100%);
  animation: ${signUpSlideAnimation} .5s ease-in-out;
  .loginSignUp-wrapper {
    justify-content: flex-start;
    &:before {
      background: #17936f url(${heroMap});
    }
  }
`;

const StyledDiv = styled.div`
  position: absolute;
  display: flex;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  
  @media screen and (max-width: 575px) {
    display: none;
  }
  
  ${props => (props.signUp ? signUpStyles : loginStyles)};
  
  .loginSignUp-wrapper {
    position: relative;
    display: flex;
    width: 100%;
    height: 100%;
    
    &:before {
      content: ' ';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-size: 40%;
      background-position-y: 90%;
      background-repeat: no-repeat;
    }
  }
    
  .loginSignUp-content {
    width: 34%;
    display: flex;
    flex-direction: column;
    align-items: center;
    align-self: center;
    justify-content: center;
    margin: auto 0;
    animation: ${props => (props.signUp ? signUpContentSlideAnimation : loginContentSlideAnimation)} .5s ease-in-out;
    z-index: 2;
    
    svg {
      height: 90px;
      margin-bottom: 60px;
      fill: #fff;
    }
    
    h3 {
      margin-bottom: 21px;
      color: #efefef;
      font-size: 20px;
      font-weight: 500;
      line-height: 24px;
    }
    
    p {
      margin-bottom: 25px;
      color: #efefef;
      font-size: 14px;
      line-height: 20px;
      text-align: center;
    }
  }
  
  .action-button {
    width: 160px;
    min-width: 160px;
    margin-bottom: 0;
    border: 1px solid #fff;
    background: rgba(0, 0, 0, 0);
    
    &:focus {
      border: 1px solid #fff;
    }
    
    &:hover {
      border: 2px solid #fff;
      background: rgba(0, 0, 0, 0);
      font-weight: 300;
    }
  }
`;

const Messages = defineMessages({
  loginH3: { id: 'auth.havent_account', defaultMessage: '!Don\'t Have an Account?' },
  signUpH3: { id: 'auth.have_account', defaultMessage: '!Already Signed up?' },
  loginP: { id: 'auth.join_earn', defaultMessage: '!Join 2key Now...' },
  signUpP: { id: 'auth.login_earn', defaultMessage: '!Continue earning your...' },
  signUpBtn: { id: 'auth.login', defaultMessage: '!Login' },
  loginBtn: { id: 'auth.join', defaultMessage: '!Join' },
});

const LoginSignUp = ({
  className,
}) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const signUp = /^\/?signup/.test(pathname);
  const onClick = useCallback(() => {
    history.push(signUp ? '/login' : '/signup');
  }, [signUp]);
  return (
    <StyledDiv className={className} signUp={signUp}>
      <div className="loginSignUp-wrapper">
        <div className="loginSignUp-content">
          <TwoKeyIcon />
          <h3><FormattedMessage id={Messages[`${signUp ? 'signUp' : 'login'}H3`].id} /></h3>
          <p><FormattedMessage id={Messages[`${signUp ? 'signUp' : 'login'}P`].id} values={{ br: <br /> }} /></p>
          <ActionButton
            label={<FormattedMessage id={Messages[`${signUp ? 'signUp' : 'login'}Btn`].id} />}
            action={onClick}
          />
        </div>
      </div>
    </StyledDiv>
  );
};

LoginSignUp.propTypes = {
  className: PropTypes.string,
};

LoginSignUp.defaultProps = {
  className: '',
};

export default LoginSignUp;
